/**
 * A held row's own-field writes (`SheetRow.Handle`'s `update`,
 * `updateDurable`, `mergeAttributes`, `setLookupKey`) and the formula
 * rewrites a rename propagates: the row's one writer (`RowWriter`) and what
 * it asks of the row — the ledger entry, the transport, the rename, the
 * follow-through of a landed write and the re-read after a failed one — a
 * parent-stored virtual row's forward to its parent, and a non-editable
 * sheet's local height.
 *
 * It reads the row only through its port (`RowUpdates.Port`), handed to it
 * by the facade that owns it, and names the backing row structurally
 * (`RowUpdates.Row`).
 */
import type { Option, RowLocalId, Unsubscribe } from "@tento-lona/sheets";
import { Json, RowNetworkKey } from "@tento-lona/sheets";
import type { Rational } from "@tento-core/rational";
import type { ResponseError } from "@tento-core/typed-api";
import type { Result } from "@tento-core/result";
import { RowWriter, RowWrites } from "@tento-lona/sheets/internal-sdk";
import { Network } from "../network.ts";
import { type RowWriteError, SdkRequestError } from "../errors.ts";
import { type Actor, RowDataChanged, RowHeightChanged } from "../invalidation.ts";
import { RowFieldWrites } from "./row-field-writes.ts";

/**
 * **SDK-internal**: one formula rewrite a rename propagates
 * (`RowMirror.Renamed`), already painted and entered in the row-field
 * ledger (`write`, a member of the rename's write group): `takeBack` returns
 * the row to what it showed before (the label was refused — nothing was
 * sent; `err` is the label's error), `send` PATCHes it and settles it on the
 * verdict (a refusal takes it back, re-reads the row and rejects with
 * `RowWriteError("update")`).
 */
export type FormulaRewrite = {
  readonly write: RowWrites.Write;
  takeBack(err: unknown): void;
  send(mode: "checked" | "durable"): Promise<void>;
};

/** A held row's own-field writes over its port. */
export class RowUpdates {
  readonly #port: RowUpdates.Port;
  /** The row's one writer (`RowWriter`): its field writes, and the promote its cell writes await. */
  readonly writer: RowWriter;

  constructor(port: RowUpdates.Port) {
    this.#port = port;
    this.writer = new RowWriter(port.route.writes().ledger, {
      key: () => port.state.key(),
      promoted: () => port.state.promoted(),
      persisted: (rowKey) => this.#persisted(rowKey),
      begin: (change, actor, quiet) => this.begin(change, actor, quiet),
      transport: (mode, rowKey) => this.transport(mode, rowKey),
      renamed: (change, landed, mode, actor) => this.#renamed(change, landed, mode, actor),
      recover: (error, rowKey) => port.failures.afterFailedWrite(error, () => this.reread(rowKey)),
      accepted: (write, answer, actor, dataKeyChanged) => this.#accepted(write, answer, actor, dataKeyChanged),
    });
  }

  get #row(): RowUpdates.Row {
    return this.#port.state.row();
  }

  get #writes(): RowFieldWrites {
    return this.#port.route.writes();
  }

  /**
   * Enter `change` in the client's row-field ledger as a write of this row
   * — shown in every copy of the row and announced as `actor` before this
   * returns (attribute keys deferred when `quiet`: `#accepted` announces
   * them); a take-back is announced as `SheetRow.Handle.ACTOR`.
   */
  begin(change: RowWrites.Change, actor: Actor, quiet: boolean): RowWrites.Write {
    const row = this.#row;
    this.#writes.hold(row);
    return this.#writes.ledger.begin(row.id, change, actor, {
      correction: this.#port.correction,
      quietAttributes: quiet,
      capture: () => RowFieldWrites.fieldsOf(this.#row),
    });
  }

  /** The transport of this row's writes: checked (`Backend.updateRow`) or durable (the client's debounce executor), addressed by `rowKey`. */
  transport(mode: "checked" | "durable", rowKey: RowNetworkKey): RowWrites.Transport {
    if (mode === "checked") return this.#writes.transport(() => rowKey);
    return this.#port.route.durableTransport(rowKey);
  }

  /**
   * A rename's formula rewrites (`RowMirror.Renamed`): painted before the
   * label, sent once `landed` (the label's verdict) resolves, taken back
   * with its refusal. `null` when `change` renames nothing.
   */
  #renamed(
    change: RowWrites.Change,
    landed: Promise<void>,
    mode: "checked" | "durable",
    actor: Actor,
  ): Option<Promise<void>> {
    const rename = renameOf(this.#row.raw.label ?? null, change.set);
    return rename == null ? null : this.#port.route.renamed({ ...rename, landed, mode, actor });
  }

  /**
   * A landed write's follow-through: when it changed an attribute the
   * row's data depends on (`descriptor.dataKeys`, announced then — deferred
   * at `begin`), or the server says the row's cells changed with it
   * (`PatchRowResponse.shouldRefetchData`), the row's cached cells are
   * dropped and the row announced, so the rebind fetches against the new
   * attributes.
   */
  #accepted(write: RowWrites.Write, answer: RowWrites.Answer, actor: Actor, dataKeyChanged: boolean): void {
    if (!dataKeyChanged && !answer.shouldRefetchData) return;
    this.#port.local.dropCachedData();
    if (dataKeyChanged) this.#writes.ledger.announce(write, actor);
    else this.#port.local.announce(new RowDataChanged(actor, this.#row.id, null));
  }

  /** Whether `change` writes an attribute the row's data depends on. */
  #changesDataKey(change: RowWrites.Change): boolean {
    const dataKeys = this.#row.descriptor.dataKeys;
    if (!dataKeys?.length) return false;
    const keys = [
      ...Object.keys(Json.object(change.set.attributes ?? null) ?? {}),
      ...Object.keys(change.attributes?.bag ?? {}),
      ...(change.attributes?.unset ?? []),
    ];
    return keys.some((k) => dataKeys.includes(k));
  }

  /** `update` of a ledger change — the row's one checked write path. */
  async update(change: RowWrites.Change, actor: Actor): Promise<void> {
    change = this.#clampHeight(change);
    if (this.#keepsHeightLocally(change, actor)) return;
    const ledger = this.#writes.ledger;
    const parent = this.#port.nesting.virtualParent();
    if (parent != null) {
      // A parent-stored virtual row: the parent's PATCH carries the
      // change. Paints first like a durable row: entered in the ledger,
      // shown and announced as `actor`, the parent's update awaited, and a
      // refusal takes back this write's own contribution (as the SDK,
      // never as `actor`).
      const write = this.begin(change, actor, false);
      try {
        await parent.update(this.#nestedChange(change), actor);
      } catch (err) {
        ledger.refuse(write, err, "");
        // after-failed-write: the parent's own `update` re-reads the parent row, whose attributes store this one.
        throw err;
      }
      // The accepted bag carried this row's nested entry — the fields of
      // its older writes still in flight too: the server holds them now,
      // so a later refusal of one of those writes keeps them.
      ledger.confirm(this.#row.id, nestedFields(change));
      ledger.accept(write);
      return;
    }
    if (this.#port.state.isPending()) {
      const refusal = this.#port.pending.pendingRefusal();
      if (refusal != null) throw refusal;
      await this.#port.pending.submitPending(change, actor, "checked");
      return;
    }
    await this.writer.update(change, actor, "checked", this.#changesDataKey(change));
  }

  /**
   * `updateDurable` of a ledger change, once the row was found live:
   * `shown` when it is applied at once (else nothing is, and `settled`
   * rejects), `settled` the verdict.
   */
  durable(change: RowWrites.Change, actor: Actor): { readonly shown: boolean; readonly settled: Promise<void> } {
    change = this.#clampHeight(change);
    if (this.#keepsHeightLocally(change, actor)) return { shown: true, settled: Promise.resolve() };
    const ledger = this.#writes.ledger;
    const parent = this.#port.nesting.virtualParent();
    if (parent != null) {
      // A parent-stored virtual row: the parent's durable PATCH carries
      // the change. Applied and announced here at once, entered in the
      // ledger, so a refusal takes back this write's own contribution
      // (as the SDK, never as `actor`).
      const forwarded = parent.updateDurable(this.#nestedChange(change), actor);
      const write = this.begin(change, actor, false);
      return {
        shown: true,
        settled: forwarded.settled.then(
          () => {
            // As the checked form: the landed bag's nested entry is held.
            ledger.confirm(this.#row.id, nestedFields(change));
            ledger.accept(write);
          },
          (err: unknown) => {
            ledger.refuse(write, err, "");
            throw err;
          },
        ),
      };
    }
    if (this.#port.state.isPending()) {
      const refusal = this.#port.pending.pendingRefusal();
      if (refusal != null) return { shown: false, settled: Promise.reject(refusal) };
      return { shown: true, settled: this.#port.pending.submitPending(change, actor, "durable") };
    }
    return { shown: true, settled: this.writer.update(change, actor, "durable", this.#changesDataKey(change)) };
  }

  /** A lookup-seeded row of a sheet persisted first (`CellAccessor.ensurePersisted`), at its position. */
  #persisted(rowKey: RowNetworkKey): Promise<void> {
    const cells = this.#port.route.sheetCells();
    return cells == null ? Promise.resolve() : cells.ensurePersisted(rowKey, this.#port.state.position());
  }

  /**
   * A rename's rewrite of this row's formula (`beginFormulaRewrite`):
   * entered in the row-field ledger and painted as `actor` now.
   */
  beginFormulaRewrite(formula: string, actor: Actor): FormulaRewrite {
    const ledger = this.#writes.ledger;
    const write = this.begin(RowWrites.set({ formula }), actor, false);
    let settled = false;
    const takeBack = (err: unknown): void => {
      if (settled) return;
      settled = true;
      ledger.refuse(write, err, "");
    };
    return {
      write,
      takeBack,
      send: async (mode) => {
        const rowKey = RowNetworkKey.fromWire(this.#row.id.toString());
        try {
          await ledger.send(write, this.transport(mode, rowKey));
        } catch (err) {
          const error = RowWrites.wrap(err, "");
          takeBack(error);
          this.#port.failures.afterFailedWrite(error, () => this.reread(rowKey));
          throw error;
        }
        if (settled) return;
        settled = true;
        ledger.accept(write);
      },
    };
  }

  /**
   * A failed PATCH of the row's recovery (`afterFailedWrite`, owned by the
   * row's `row.update` owner): the row's re-read (`rereadRow`) — an answer
   * that arrives after the row's owners were disposed is dropped — its view
   * shown as the SDK's correction.
   */
  reread(rowKey: RowNetworkKey): Promise<void> {
    const port = this.#port;
    return rereadRow(port.state.row(), port.route.writes(), port.route, rowKey, port.failures.updateOwner(), port.correction);
  }

  /**
   * A non-editable sheet keeps a height change in the local row-height
   * preferences (nothing is sent, no row write). Returns whether `change`
   * was that.
   */
  #keepsHeightLocally(
    change: RowWrites.Change,
    actor: Actor,
  ): boolean {
    const height = change.set.heightPx;
    if (this.#port.state.editable() || height == null) return false;
    const row = this.#row;
    row.raw.heightPx = height;
    this.#port.local.rowHeightPrefs()?.set(row.id, height, actor);
    this.#port.local.announce(new RowHeightChanged(actor, row.id));
    return true;
  }

  /** `change` with `heightPx` clamped to the descriptor's bounds. */
  #clampHeight(
    change: RowWrites.Change,
  ): RowWrites.Change {
    const height = change.set.heightPx;
    if (height == null) return change;
    const bounds = this.#row.descriptor.heightBounds;
    if (!bounds) return change;
    return {
      ...change,
      set: { ...change.set, heightPx: Math.round(Math.max(bounds.min, Math.min(bounds.max, height))) },
    };
  }

  /**
   * The parent's write that carries this virtual row's `change`: its
   * editable fields deep-merged into `nestedRows[preset]` of the parent's
   * projected bag.
   */
  #nestedChange(change: RowWrites.Change): RowWrites.Change {
    const preset = this.#port.nesting.nestedPreset();
    return RowWrites.mergeAttributes("deep", { nestedRows: { [preset]: nestedRowUpdates(change.set) } }, []);
  }
}

export namespace RowUpdates {
  /** The backing row as the field writes read it: its ledger copy, its descriptor's bounds and data keys. */
  export interface Row extends RowFieldWrites.Copy {
    readonly descriptor: {
      readonly dataKeys?: readonly string[];
      readonly heightBounds?: { readonly min: number; readonly max: number };
    };
  }

  /** A rename to propagate (`RowMirror.Renamed`), sent once `landed` resolves. */
  export type Renamed = {
    readonly from: string;
    readonly to: string;
    readonly landed: Promise<void>;
    readonly mode: "checked" | "durable";
    readonly actor: Actor;
  };

  /** A parent-stored virtual row's parent, which carries the row's writes. */
  export interface Parent {
    update(change: RowWrites.Change, actor: Actor): Promise<void>;
    updateDurable(change: RowWrites.Change, actor: Actor): { readonly settled: Promise<void> };
  }

  /** A pending draft's persist: the change buffered and submitted with (or after) its promote. */
  export type Pending = (change: RowWrites.Change, actor: Actor, mode: "checked" | "durable") => Promise<void>;

  /**
   * What the field writes read of their row (`SheetRow.Handle`), by role,
   * each member answered when it is asked: the row's state, the route a write
   * takes, a virtual row's nesting, a pending row's persist, the row's local
   * effects and how a failed write recovers. No role hands out the client's
   * API or its invalidation sink.
   */
  export interface Port {
    /** The actor a take-back is announced as (`SheetRow.Handle.ACTOR`). */
    readonly correction: Actor;
    readonly state: RowState;
    readonly route: WriteRoute;
    readonly nesting: Nesting;
    readonly pending: PendingRow;
    readonly local: LocalEffects;
    readonly failures: Failures;
  }

  /** The row as a write addresses it. */
  export interface RowState {
    /** The backing row (the registry's, or the one the latest joined tree bridged). */
    row(): Row;
    /** The row's wire key as it is now (`RowWriter.Port.key`). */
    key(): RowNetworkKey;
    /** The sheet's COW promote to await, or `null` (`RowWriter.Port.promoted`). */
    promoted(): Option<Promise<RowNetworkKey>>;
    /** Where the row sits in its sheet. */
    position(): Option<Rational>;
    /** Whether the row's sheet allows edits. */
    editable(): boolean;
    /** Whether the row is not yet persisted (a pending draft, or a pending row of a sheet). */
    isPending(): boolean;
  }

  /** The routes a row write takes. */
  export interface WriteRoute {
    /** The client's row-field writes (`RowWrites` ledger and transport). */
    writes(): RowFieldWrites;
    /** The durable transport: the sheet's cell writer's, else the client's debounce executor. */
    durableTransport(rowKey: RowNetworkKey): RowWrites.Transport;
    /** The sheet's cell writer's persist of a lookup-seeded row, or `null` (none). */
    sheetCells(): Option<{ ensurePersisted(rowKey: RowNetworkKey, position: Option<Rational>): Promise<void> }>;
    /** A rename's rewrites through the client's mirror; `null` when no formula names the row. */
    renamed(rename: Renamed): Option<Promise<void>>;
    /** GET the row keyed `rowKey` (`/api/rows/{rowKey}`), a failed PATCH's re-read: the route's only read. */
    getRow(rowKey: RowNetworkKey): Promise<Result<Option<Network.SheetRowDb>, ResponseError>>;
  }

  /** A parent-stored virtual row's nesting. */
  export interface Nesting {
    /** The parent a parent-stored virtual row forwards its writes to, or `null`. */
    virtualParent(): Option<Parent>;
    /** A parent-stored virtual row's preset (its key in the parent's `nestedRows`). */
    nestedPreset(): string;
  }

  /** A row not yet persisted. */
  export interface PendingRow {
    /** The refusal of an update of a pending row of a sheet (it has no persist of its own), else `null`. */
    pendingRefusal(): Option<RowWriteError>;
    /** A pending draft's persist: the change buffered and submitted with (or after) its promote. */
    readonly submitPending: Pending;
  }

  /** What a write changes beside the server: the row's cache, its local height preference, its repaint. */
  export interface LocalEffects {
    /** Drop the row's cached cells (a standalone row's cache is not minted for it). */
    dropCachedData(): void;
    /** The sheet's local row-height preferences, or `null`. */
    rowHeightPrefs(): Option<{ set(id: RowLocalId, heightPx: number, actor: Actor): void }>;
    /** Announce the row's repaint (its data, or its height). */
    announce(event: RowDataChanged | RowHeightChanged): void;
  }

  /** How a failed write recovers. */
  export interface Failures {
    /** The owner a failed PATCH's re-read is watched under (`row.update`), or `null`. */
    updateOwner(): Option<{ watch(onDisposed: (err: Error) => void): Unsubscribe }>;
    /** A failed PATCH's recovery under the row's `row.update` owner (`afterFailedWrite`). */
    afterFailedWrite(error: unknown, recover: () => Promise<void>): void;
  }

}

/**
 * The rename `patch` makes of a row labelled `before` — `null` when it sets
 * no label, when either label is empty, or when they are equal.
 */
function renameOf(before: Option<string>, patch: RowWrites.Fields): Option<{ readonly from: string; readonly to: string }> {
  if (patch.label === undefined) return null;
  const to = patch.label ?? null;
  if (before == null || before === "" || to == null || to === "" || before === to) return null;
  return { from: before, to };
}

/**
 * The editable properties a parent-stored virtual row keeps in its
 * parent's `attributes.nestedRows[preset]`, taken from a row patch.
 */
function nestedRowUpdates(
  patch: RowWrites.Fields,
): { [key: string]: Json } {
  const updates: { [key: string]: Json } = {};
  if (patch.label !== undefined) updates.label = patch.label ?? null;
  if (patch.timeScale !== undefined) updates.timeScale = patch.timeScale ?? null;
  if (patch.heightPx !== undefined) updates.heightPx = patch.heightPx ?? null;
  return updates;
}

/**
 * The fields of a parent-stored virtual row's `change` its parent's
 * accepted bag carried (`nestedRowUpdates`): confirmed on the row once the
 * parent's write landed, so a later refusal of an older write of the row
 * no longer takes them back.
 */
function nestedFields(change: RowWrites.Change): RowWrites.Fields {
  const set = change.set;
  return {
    ...(set.label !== undefined ? { label: set.label } : {}),
    ...(set.heightPx !== undefined ? { heightPx: set.heightPx } : {}),
    ...(set.timeScale !== undefined ? { timeScale: set.timeScale } : {}),
  };
}

/**
 * A failed PATCH of a row's recovery (`afterFailedWrite`): `GET
 * /api/rows/{rowKey}`, as a durable update's refusal is re-read, so a write
 * that did land comes back. The read goes through the client's row-field
 * ledger (`RowWrites.beginRead` / `endRead`): the server's row becomes the
 * row's confirmed state, every write still in flight stays applied over it,
 * and what changed is shown in every copy of the row and announced as the
 * SDK's correction (`correction`, `SheetRow.Handle.ACTOR`). `owner` is the
 * row's owning lifecycle (the row's sheet, else the client; `null` for a
 * joined tree no sheet owns): `afterFailedWrite` sends nothing once it was
 * disposed, and an answer that arrives after it was disposed is dropped
 * here (the read closed as a failed one, nothing shown).
 */
async function rereadRow(
  row: RowFieldWrites.Copy,
  writes: RowFieldWrites,
  route: Pick<RowUpdates.WriteRoute, "getRow">,
  rowKey: RowNetworkKey,
  owner: Option<{ watch(onDisposed: (err: Error) => void): Unsubscribe }>,
  correction: Actor,
): Promise<void> {
  let retired = false;
  const unwatch = owner?.watch(() => {
    retired = true;
  }) ?? null;
  writes.hold(row);
  const read = writes.ledger.beginRead(row.id);
  let fresh: Option<Network.SheetRowDb> = null;
  try {
    fresh = await SdkRequestError.checkRequest(
      "rows",
      "get",
      "GET",
      "/api/rows/{rowKey}",
      () =>
        SdkRequestError.notFoundAsNull(() =>
          route.getRow(RowNetworkKey.forRead(rowKey))
        ),
    );
  } finally {
    unwatch?.();
    // Disposed while the read was out: closed as a failed read, so the
    // answer is neither confirmed nor shown.
    if (retired) fresh = null;
    writes.ledger.endRead(read, fresh == null ? null : rereadFields(fresh, row), correction);
  }
}

/**
 * A server row's own fields as the row-field ledger confirms them (a
 * re-read): every field the row PATCH writes, `null` when unset (the time
 * scale falls back to the row's own when the answer carries none).
 *
 * @internal SDK-private (`LonaSdk.Client`'s row re-read).
 */
export function rereadFields(fresh: Network.SheetRowDb, row: { readonly raw: Network.SheetRowDb }): RowWrites.Fields {
  return {
    label: fresh.label ?? null,
    heightPx: fresh.heightPx ?? null,
    formula: fresh.formula ?? null,
    attributes: Json.object(fresh.attributes ?? null),
    icon: fresh.icon ?? null,
    timeScale: fresh.timeScale ?? row.raw.timeScale ?? null,
    lookupKey: fresh.lookupKey ?? null,
  };
}
